import "./Settings.css";
import { type FormEvent, useEffect, useState } from "react";
import { api, type Me } from "../../api/client.js";
import { ShortcutSheet } from "../../builder/components/Shortcuts.js";
import { Switch } from "../../builder/components/ui.js";
import { PageHeader } from "../../kit/index.js";
import { usePortal, useTitle } from "../context.js";
import { errorMessage, matesTraining, setMatesTraining } from "../data.js";
import { setTheme, setUnits, setUseAi, usePrefs, type Theme, type UnitChoice } from "../prefs.js";
import { useTour } from "../../tour/Tour.js";

interface Browser {
  id: string;
  label: string;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

function Segmented<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="bp-segmented" role="radiogroup" aria-label={label}>
      {options.map(([v, text]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={"bp-segment" + (value === v ? " is-on" : "")} onClick={() => onChange(v)}>
          {text}
        </button>
      ))}
    </div>
  );
}

const seen = (iso: string) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

/** About the person signed in (DESIGN §16.7). */
export function Settings() {
  useTitle("Settings");
  const { me, setMe, announce } = usePortal();
  const prefs = usePrefs();
  const tour = useTour();
  const [name, setName] = useState(me.person.name);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [browsers, setBrowsers] = useState<Browser[] | null>(null);
  const [browserError, setBrowserError] = useState<string | null>(null);
  const [shortcuts, setShortcuts] = useState(false);
  const [mates, setMates] = useState(() => matesTraining(me));
  const [matesError, setMatesError] = useState<string | null>(null);

  const loadBrowsers = () =>
    api<{ browsers: Browser[] }>("/web/api/browsers").then(
      (r) => {
        setBrowsers([...r.browsers].sort((a, b) => Number(b.current) - Number(a.current)));
        setBrowserError(null);
      },
      (e: unknown) => setBrowserError(errorMessage(e)),
    );
  useEffect(() => {
    void loadBrowsers();
  }, []);

  const save = (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setNameError(null);
    api<Me>("/web/api/me", { method: "PATCH", body: { name } }).then(
      (updated) => {
        setSaving(false);
        setMe(updated);
        announce("Name saved");
      },
      (err: unknown) => {
        setSaving(false);
        setNameError(errorMessage(err));
      },
    );
  };

  const toggleMates = (on: boolean) => {
    setMates(on);
    setMatesError(null);
    setMatesTraining(on).then(
      (saved) => {
        setMates(saved);
        setMe({ ...me, team: { ...me.team, matesTraining: saved } });
      },
      (err: unknown) => {
        setMates(!on);
        setMatesError(errorMessage(err));
      },
    );
  };

  const signOut = (id?: string) =>
    api(id ? `/web/api/browsers/${encodeURIComponent(id)}` : "/web/api/browsers", { method: "DELETE" }).then(
      () => {
        announce(id ? "Signed out" : "Signed out of all other browsers");
        return loadBrowsers();
      },
      (e: unknown) => setBrowserError(errorMessage(e)),
    );

  const others = (browsers ?? []).filter((b) => !b.current);
  const trimmed = name.trim();
  return (
    <div className="k-page set-page">
      <PageHeader title="Settings" />

      <section className="set-section" aria-labelledby="set-you">
        <h2 id="set-you">You</h2>
        <form className="set-card set-name" onSubmit={save}>
          <label htmlFor="set-name">Name</label>
          <input id="set-name" className="k-input" value={name} maxLength={60} autoComplete="name" onChange={(e) => setName(e.target.value)} aria-describedby="set-name-help" />
          <button type="submit" className="btn" disabled={saving || !trimmed || trimmed === me.person.name}>
            {saving ? "Saving…" : "Save"}
          </button>
          <p id="set-name-help" className="set-help">
            Athletes and other coaches see this name.
          </p>
          {nameError ? (
            <p className="set-error" role="alert">
              {nameError}
            </p>
          ) : null}
        </form>
      </section>

      {me.person.isAdmin && mates != null ? (
        <section className="set-section" aria-labelledby="set-team">
          <h2 id="set-team">Team</h2>
          <div className="set-card">
            <div className="set-row">
              <span>Teammates see each other's training</span>
              <Switch label="Teammates see each other's training" checked={mates} onChange={toggleMates} />
            </div>
            <p className="set-help">Athletes see every group's week. Those who turn on Share training also see who has done it, and each other's sets during the day's workouts. Notes are never shown.</p>
            {matesError ? (
              <p className="set-error" role="alert">
                {matesError}
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="set-section" aria-labelledby="set-look">
        <h2 id="set-look">Appearance</h2>
        <div className="set-card">
          <div className="set-row">
            <span id="set-theme">Theme</span>
            <Segmented<Theme>
              label="Theme"
              value={prefs.theme}
              options={[
                ["system", "System"],
                ["light", "Light"],
                ["dark", "Dark"],
              ]}
              onChange={setTheme}
            />
          </div>
          <div className="set-row">
            <span>Weights</span>
            <Segmented<UnitChoice>
              label="Weights"
              value={prefs.units}
              options={[
                ["logged", "As logged"],
                ["kg", "kg"],
                ["lb", "lb"],
              ]}
              onChange={setUnits}
            />
          </div>
          <p className="set-help">Kept in this browser. Changing the unit converts what the portal shows; nothing logged changes.</p>
        </div>
      </section>

      <section className="set-section" aria-labelledby="set-ai">
        <h2 id="set-ai">AI</h2>
        <div className="set-card">
          <div className="set-row">
            <span>Use AI</span>
            <Switch label="Use AI" checked={prefs.useAi} onChange={setUseAi} />
          </div>
          <p className="set-help">The assistant and the line checks. Sends your week's lines and its column names; nothing is kept. Off, the Guide is still there. Kept in this browser.</p>
        </div>
      </section>

      <section className="set-section" aria-labelledby="set-browsers">
        <h2 id="set-browsers">Signed-in browsers</h2>
        <div className="set-card">
          {browserError ? (
            <p className="set-error" role="alert">
              {browserError}
            </p>
          ) : null}
          {browsers == null && !browserError ? <p className="set-help">Loading…</p> : null}
          {browsers ? (
            <ul className="set-browsers">
              {browsers.map((b) => (
                <li key={b.id} className="set-browser">
                  <div className="set-browser-text">
                    <span className="set-browser-label">{b.label}</span>
                    <span className="set-help">{b.current ? "This browser" : "Last used " + seen(b.lastSeenAt)}</span>
                  </div>
                  {b.current ? null : (
                    <button type="button" className="btn set-remove" aria-label={`Sign out ${b.label}`} onClick={() => void signOut(b.id)}>
                      Sign out
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          {others.length > 1 ? (
            <button type="button" className="btn set-all set-remove" onClick={() => void signOut()}>
              Sign out of all others
            </button>
          ) : null}
        </div>
      </section>

      <section className="set-section" aria-labelledby="set-help">
        <h2 id="set-help">Help</h2>
        <div className="set-card set-help-actions">
          <button type="button" className="btn" onClick={tour.pick}>
            Take the tour
          </button>
          <button type="button" className="btn" onClick={() => setShortcuts(true)}>
            Keyboard shortcuts
          </button>
        </div>
      </section>
      <ShortcutSheet open={shortcuts} onClose={() => setShortcuts(false)} />
    </div>
  );
}
